import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import styles from './MetricStackedBarChart.module.css';
import Button from '@/components/forms/Button/Button';
import Skeleton from '@/components/Skeleton/Skeleton'; // Assuming Skeleton is imported correctly
import Flex from '../Flex/Flex';
import { dayjsInUTC } from '@/utils/dateUtils';

// --- Define Icons Outside Component ---
const UpArrowIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginRight: '4px', verticalAlign: 'middle' }} // Added verticalAlign
    >
        <path
            d="M12 4L12 20M12 4L18 10M12 4L6 10" // Simpler up arrow
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const PrevIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            d="M8.86719 11.5381L5.80052 8.47142C5.73385 8.40475 5.68674 8.33253 5.65919 8.25475C5.63119 8.17697 5.61719 8.09364 5.61719 8.00475C5.61719 7.91586 5.63119 7.83253 5.65919 7.75475C5.68674 7.67697 5.73385 7.60475 5.80052 7.53809L8.86719 4.47142C8.98941 4.3492 9.14497 4.28809 9.33385 4.28809C9.52274 4.28809 9.6783 4.3492 9.80052 4.47142C9.92274 4.59364 9.98385 4.7492 9.98385 4.93809C9.98385 5.12697 9.92274 5.28253 9.80052 5.40475L7.20052 8.00475L9.80052 10.6048C9.92274 10.727 9.98385 10.8825 9.98385 11.0714C9.98385 11.2603 9.92274 11.4159 9.80052 11.5381C9.6783 11.6603 9.52274 11.7214 9.33385 11.7214C9.14497 11.7214 8.98941 11.6603 8.86719 11.5381Z"
            fill="currentColor"/>
    </svg>
);

const NextIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            d="M7.13281 11.5381L10.1995 8.47142C10.2661 8.40475 10.3133 8.33253 10.3408 8.25475C10.3688 8.17697 10.3828 8.09364 10.3828 8.00475C10.3828 7.91586 10.3688 7.83253 10.3408 7.75475C10.3133 7.67697 10.2661 7.60475 10.1995 7.53809L7.13281 4.47142C7.01059 4.3492 6.85503 4.28809 6.66615 4.28809C6.47726 4.28809 6.3217 4.3492 6.19948 4.47142C6.07726 4.59364 6.01615 4.7492 6.01615 4.93809C6.01615 5.12697 6.07726 5.28253 6.19948 5.40475L8.79948 8.00475L6.19948 10.6048C6.07726 10.727 6.01615 10.8825 6.01615 11.0714C6.01615 11.2603 6.07726 11.4159 6.19948 11.5381C6.3217 11.6603 6.47726 11.7214 6.66615 11.7214C6.85503 11.7214 7.01059 11.6603 7.13281 11.5381Z"
            fill="currentColor"/>
    </svg>
);
// --- Types ---
type DataPoint = {
    [key: string]: string | number;
};

type Metric = {
    key: string;
    label: string;
    color: string;
    total: number | string; // Allow string for formatted totals
    change?: {
        value: number;
        label: string;
    };
};

type MetricStackedBarChartProps = {
    data: DataPoint[] | null | undefined;
    metrics: Metric[]; // Still expect exactly two metrics
    xAxisKey?: string;
    height?: string;
    isLoading?: boolean; // Added isLoading prop
    className?: string; // Allow passing custom class
};

// --- Helper Functions ---
const findFirstNonZeroIndex = (
    data: DataPoint[],
    key1: string,
    key2: string
): number => {
    if (!data) return -1;
    return data.findIndex(item => (Number(item[key1]) || 0) > 0 || (Number(item[key2]) || 0) > 0);
};

// --- Custom Tooltip Component ---
const CustomTooltip = ({ active, payload, label, metrics }: TooltipProps<ValueType, NameType> & { metrics: Metric[] }) => {
    if (active && payload && payload.length && metrics.length === 2) {
        const [primaryMetric, secondaryMetric] = metrics;
        // Find payload data corresponding to metrics
        const primaryPayload = payload.find(p => p.dataKey === primaryMetric.key);
        const secondaryPayload = payload.find(p => p.dataKey === secondaryMetric.key);

        // Calculate total for the stacked bar
        const totalValue = (Number(primaryPayload?.value) || 0) + (Number(secondaryPayload?.value) || 0);

        return (
            <div className={styles.customTooltip}>
                <p className={styles.tooltipLabel}>{`${dayjsInUTC(label).format('MMM DD YYYY')}`}</p>
                {primaryPayload && (
                    <p style={{ color: primaryMetric.color }}>
                        {`${primaryMetric.label}: ${primaryPayload.value}`}
                    </p>
                )}
                {secondaryPayload && (
                     <p style={{ color: secondaryMetric.color }}>
                        {`${secondaryMetric.label}: ${secondaryPayload.value}`}
                    </p>
                )}
                 <p className={styles.tooltipTotal}>
                    {`Total: ${totalValue}`}
                </p>
            </div>
        );
    }
    return null;
};


// --- Main Component ---
const MetricStackedBarChart = ({
    data,
    metrics,
    xAxisKey = 'day',
    height = "230px",
    isLoading = false, // Default value for isLoading
    className = '',
}: MetricStackedBarChartProps) => {

    const [startIndex, setStartIndex] = useState(0);
    const visibleBars = 16; // Consider making this a prop if needed

    // Basic validation for metrics prop
    const isValidMetrics = metrics && metrics.length === 2;
    const primaryMetric = isValidMetrics ? metrics[0] : null;
    const secondaryMetric = isValidMetrics ? metrics[1] : null;

    // Memoize keys to prevent unnecessary effect triggers
    const primaryKey = primaryMetric?.key;
    const secondaryKey = secondaryMetric?.key;

    // Effect to reset/adjust startIndex when data or metric keys change
    useEffect(() => {
        if (data && primaryKey && secondaryKey) {
            // Option 1: Always reset to the start
            // setStartIndex(0);

            // Option 2: Try to find the first non-zero section (use useCallback for helper)
             const firstNonZero = findFirstNonZeroIndex(data, primaryKey, secondaryKey);
             const initialIndex = Math.max(0, firstNonZero); // Start at first non-zero or 0
             // Ensure initialIndex doesn't go beyond possible start points
             const maxPossibleStart = Math.max(0, data.length - visibleBars);
             setStartIndex(Math.min(initialIndex, maxPossibleStart));

        } else {
            setStartIndex(0); // Reset if data is invalid
        }
    }, [data, primaryKey, secondaryKey, visibleBars]); // Add dependencies

    const handlePrev = useCallback(() => {
        setStartIndex(prev => Math.max(0, prev - 1));
    }, []);

    const handleNext = useCallback(() => {
        if (!data) return;
        setStartIndex(prev => Math.min(data.length - visibleBars, prev + 1));
    }, [data, visibleBars]);

    // Memoize visible data slice
    const visibleData = useMemo(() => {
        if (!data || data.length === 0) return [];
        // Ensure startIndex is valid before slicing
        const safeStartIndex = Math.max(0, Math.min(startIndex, data.length - visibleBars));
        return data.slice(safeStartIndex, safeStartIndex + visibleBars);
    }, [data, startIndex, visibleBars]);

    // Calculate max index for disabling next button
    const maxStartIndex = data ? Math.max(0, data.length - visibleBars) : 0;
    const isPrevDisabled = startIndex === 0;
    const isNextDisabled = !data || startIndex >= maxStartIndex;

    if (isLoading) {
        return (
            // Consistent Skeleton structure
            <Flex direction='column' gap={4} className={styles.loadingContainer}>
                <Flex direction='row'>
                    <div className={styles.statsContainer}><Skeleton height={69} /></div>
                    <div className={styles.statsContainer}><Skeleton height={69} /></div>
                </Flex>
                <Skeleton width="100%" height={"100%"} />
            </Flex>
        )
    }

    return (
        <div className={`${styles.container} ${className}`}>
                    {/* Header with Metric Stats */}
            <div className={styles.header}>
                {/* Primary Metric */}
                <div className={styles.statsContainer}>
                    <div className={styles.titleContainer}>
                        {/* Use metric color directly if possible, else rely on CSS */}
                        <div className={styles.dot} style={{ backgroundColor: primaryMetric.color }}></div>
                        <span className={styles.label}>{primaryMetric.label}</span>
                    </div>
                    <div className={styles.value}>{primaryMetric.total}</div>
                    {primaryMetric.change && (
                        <div className={styles.change}>
                            <UpArrowIcon />
                            <span>{Math.abs(primaryMetric.change.value)}% </span> {/* Show absolute value */}
                            <span className={styles.changeText}>{primaryMetric.change.label}</span>
                        </div>
                    )}
                </div>

                {/* Secondary Metric */}
                <div className={styles.statsContainer}>
                    <div className={styles.titleContainer}>
                        <div className={styles.dot} style={{ backgroundColor: secondaryMetric.color }}></div>
                        <span className={styles.label}>{secondaryMetric.label}</span>
                    </div>
                    <div className={styles.value}>{secondaryMetric.total}</div>
                    {secondaryMetric.change && (
                        <div className={styles.change}>
                            <UpArrowIcon />
                            <span>{Math.abs(secondaryMetric.change.value)}% </span>
                            <span className={styles.changeText}>{secondaryMetric.change.label}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Chart Area */}
            <div className={styles.chartWrapper}> {/* Added wrapper for positioning buttons */}
                <div className={styles.chartContainer} style={{ height }}>
                        {visibleData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={visibleData}
                                barSize={20} // Consider making these props
                                barGap={0}
                                barCategoryGap={8} // Adjust spacing between bars
                                margin={{ top: 5, right: 5, left: -20, bottom: 0 }} // Adjust margins carefully
                                syncId="metricChartSync" // Add syncId if you have multiple charts
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    horizontal={true}
                                    vertical={false}
                                    stroke="var(--static-divider-standard, #E5E7EB)" // Use CSS var with fallback
                                />
                                <XAxis
                                    dataKey={xAxisKey}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: 'var(--static-text-secondary, #6B7280)', fontSize: 12 }}
                                    tickFormatter={(value) => dayjsInUTC(value).format('MMM D')}
                                    interval="preserveStartEnd" // Helps with label spacing
                                    // padding={{ left: 10, right: 10 }} // Recharts padding can be tricky
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: 'var(--static-text-secondary, #6B7280)', fontSize: 12 }}
                                    tickCount={5} // Adjust tick count as needed
                                    // Consider a tick formatter for large numbers
                                    // tickFormatter={(value) => `${value / 1000}k`}
                                    width={40} // Give Y-axis space
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(200, 200, 200, 0.1)' }} // Subtle cursor highlight
                                    content={<CustomTooltip metrics={metrics} />} // Use custom tooltip
                                />
                                {/* Bars - Order matters for stacking & radius */}
                                <Bar
                                    dataKey={secondaryMetric.key}
                                    stackId="a"
                                    fill={secondaryMetric.color}
                                    radius={[0, 0, 0, 0]} // Bottom bar - no radius
                                />
                                <Bar
                                    dataKey={primaryMetric.key}
                                    stackId="a"
                                    fill={primaryMetric.color}
                                    radius={[4, 4, 0, 0]} // Top bar - top radius
                                />
                            </BarChart>
                        </ResponsiveContainer>
                        ) : (
                            <div className={styles.noChartData}>No data for selected period</div>
                        )}
                </div>

                {/* Navigation Buttons */}
                <Button
                    type={"text"}
                    onClick={handlePrev}
                    disabled={isPrevDisabled}
                    className={`${styles.navButton} ${styles.leftButton}`}
                    aria-label="Previous data range"
                >
                    <PrevIcon />
                </Button>
                <Button
                    type={"text"}
                    onClick={handleNext}
                    disabled={isNextDisabled}
                    className={`${styles.navButton} ${styles.rightButton}`}
                    aria-label="Next data range"
                >
                    <NextIcon />
                </Button>
            </div>
        </div>
    );
};

export default MetricStackedBarChart;